// @ts-nocheck
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import React from "react";
import { css } from "styled-components";
import translate from "@insite/client-framework/Translate";
import Typography from "@insite/mobius/Typography";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import TextField from "@insite/mobius/TextField";
import {
    CheckoutReviewAndSubmitPaymentDetailsStyles,
    checkoutReviewAndSubmitPaymentDetailsStyles,
} from "@store/Overrides/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmitPaymentDetails";
import CreditCardBillingAddressEntry from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/CreditCardBillingAddressEntry";
import CreditCardDetailsEntry from "@store/Overrides/Widgets/CheckoutReviewAndSubmit/CreditCardDetailsEntry";
import PaymentProfileBillingAddress from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/PaymentProfileBillingAddress";
import SavedPaymentProfileEntry from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/SavedPaymentProfileEntry";
import Select from "@insite/mobius/Select";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import Modal from "@store/MobiusOverrides/Modal/Modal";
import { useBunzlDispatch } from "@store/Framework/Store/Bunzl/BunzlReducer";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import { CustomWidgetModule } from "@store/Framework/Types/types";
import Button, { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Button";
import { pxToRem } from "@store/Framework/Common/Utilities/unitHelper";
import siteMessage from "@insite/client-framework/SiteMessage";
import InfosBox from "@store/Library/Components/Toolkit/InfosBox";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useAccountBalancePayment from "@store/Hooks/useAccountBalancePayment";

const StyledForm = getStyledWrapper("form");
const StyledFieldSet = getStyledWrapper("fieldset");

interface AccountBalanceModelStyles extends CheckoutReviewAndSubmitPaymentDetailsStyles {
    buttonWrapper?: InjectableCss;
    payButton?: ButtonPresentationProps;
}

const accountBalanceModelStyles: AccountBalanceModelStyles = {
    paymentMethodSelect: {
        ...checkoutReviewAndSubmitPaymentDetailsStyles.paymentMethodSelect,
        cssOverrides: {
            ...checkoutReviewAndSubmitPaymentDetailsStyles.paymentMethodSelect?.cssOverrides,
            inputSelect: css`
                ${checkoutReviewAndSubmitPaymentDetailsStyles.paymentMethodSelect?.cssOverrides?.inputSelect}
                padding-right: ${pxToRem(48)};
            `,
        },
    },
    ...checkoutReviewAndSubmitPaymentDetailsStyles,
    subSection: {
        ...checkoutReviewAndSubmitPaymentDetailsStyles.subSection,
        css: css`
            ${checkoutReviewAndSubmitPaymentDetailsStyles.subSection?.css}
            margin-bottom: ${pxToRem(30)};
        `,
    },
    creditCardDetails: {
        ...checkoutReviewAndSubmitPaymentDetailsStyles.creditCardDetails,

        cardNumberGridItem: {
            ...checkoutReviewAndSubmitPaymentDetailsStyles.creditCardDetails?.cardNumberGridItem,
            width: [12, 6, 6, 6, 6],
        },
        cardHolderNameGridItem: {
            ...checkoutReviewAndSubmitPaymentDetailsStyles.creditCardDetails?.cardHolderNameGridItem,
            width: [12, 6, 6, 6, 6],
        },
        cardTypeGridItem: {
            ...checkoutReviewAndSubmitPaymentDetailsStyles.creditCardDetails?.cardTypeGridItem,
            width: [12, 6, 6, 6, 6],
        },
        securityCodeGridItem: {
            ...checkoutReviewAndSubmitPaymentDetailsStyles.creditCardDetails?.securityCodeGridItem,
            width: [12, 6, 6, 6, 6],
        },
        expirationYearGridItem: {
            ...checkoutReviewAndSubmitPaymentDetailsStyles.creditCardDetails?.expirationYearGridItem,
            width: [12, 6, 6, 6, 6],
        },
        expirationMonthGridItem: {
            ...checkoutReviewAndSubmitPaymentDetailsStyles.creditCardDetails?.expirationMonthGridItem,
            width: [12, 6, 6, 6, 6],
        },
    },
    buttonWrapper: {
        css: css`
            display: grid;
            justify-content: center;
            gap: 1rem;
            justify-items: center;
        `,
    },
    payButton: {
        css: css`
            width: fit-content;
        `,
    },
};
const AccountBalanceModel: React.FC = () => {
    const bunzlDispatch = useBunzlDispatch();
    const styles = accountBalanceModelStyles;
    const accountBalancePayment = useAccountBalancePayment();

    if (
        !accountBalancePayment.currentPaymentMethodDto ||
        !accountBalancePayment.billToState ||
        !accountBalancePayment.countries
    ) {
        return null;
    }
    return (
        <Modal
            isOpen={accountBalancePayment.isSubmitting}
            closeOnScrimClick={false}
            headline={translate("Review and Submit Payment")}
            handleClose={() => {
                bunzlDispatch({ type: "Pages/AccountBalance/ToggleSubmitting" });
                accountBalancePayment.setErrorMessage(undefined);
            }}
            sizeVariant="medium"
        >
            <LoadingOverlay loading={accountBalancePayment.isLoading}>
                <StyledForm
                    {...styles.form}
                    id="reviewAndSubmitPaymentForm"
                    onSubmit={accountBalancePayment.handleFormSubmit}
                    noValidate={true}
                    data-test-selector="reviewAndSubmitPaymentForm"
                >
                    <StyledFieldSet {...styles.fieldset}>
                        <Typography {...styles.paymentDetailsHeading}>{translate("Payment Details")}</Typography>

                        <StyledWrapper {...styles.subSection}>
                            <GridContainer {...styles.paymentMethodAndPONumberContainer}>
                                <GridItem {...styles.paymentMethodGridItem} width={[12, 6, 6, 6, 6]}>
                                    {accountBalancePayment.paymentMethods &&
                                        accountBalancePayment.paymentMethods.length > 0 && (
                                            <StyledWrapper {...styles.gridRow}>
                                                <Select
                                                    {...styles.paymentMethodSelect}
                                                    label={translate("Payment Method")}
                                                    value={accountBalancePayment.currentPaymentMethodDto?.name}
                                                    onChange={accountBalancePayment.handlePaymentMethodChange}
                                                    required
                                                    data-test-selector="checkoutReviewAndSubmit_paymentMethod"
                                                    ref={accountBalancePayment.paymentMethodRef}
                                                    disabled={accountBalancePayment.paymentMethods.length <= 1}
                                                >
                                                    {accountBalancePayment.paymentMethods.length > 1 ? (
                                                        <option value="">{translate("Select Payment Method")}</option>
                                                    ) : null}
                                                    {accountBalancePayment.paymentMethods.map(method => (
                                                        <option key={method.name} value={method.name}>
                                                            {method.isPaymentProfile
                                                                ? `${translate("Credit Card")}${translate(":")} ${
                                                                      method.description || method.name
                                                                  }`
                                                                : method.description || method.name}
                                                        </option>
                                                    ))}
                                                </Select>
                                            </StyledWrapper>
                                        )}
                                </GridItem>
                                <GridItem {...styles.poNumberGridItem}>
                                    <StyledWrapper {...styles.gridRow}>
                                        <TextField
                                            label={
                                                <>
                                                    <span aria-hidden>{translate("Amount to Pay")}</span>
                                                    <VisuallyHidden>
                                                        {translate("Purchase Order Number")}
                                                    </VisuallyHidden>
                                                </>
                                            }
                                            value={accountBalancePayment.formattedAmount}
                                            required
                                            error={
                                                accountBalancePayment.showFormErrors &&
                                                accountBalancePayment.poNumberError
                                            }
                                            data-test-selector="checkoutReviewAndSubmit_poNumber"
                                            ref={accountBalancePayment.poNumberRef}
                                            disabled={true}
                                        />
                                    </StyledWrapper>
                                </GridItem>
                            </GridContainer>
                        </StyledWrapper>

                        {!accountBalancePayment.bypassCvvForSavedCards && (
                            <StyledWrapper {...styles.subSection}>
                                <Typography {...styles.paymentProfileDetailsHeading}>
                                    {translate("Credit Card Details")}
                                </Typography>
                                <GridContainer {...styles.paymentProfileDetailsContainer}>
                                    <GridItem {...styles.paymentProfileDetailsGridItem}>
                                        <SavedPaymentProfileEntry
                                            iframe={"Spreedly"}
                                            onSecurityCodeUpdate={accountBalancePayment.setSecurityCode}
                                            extendedStyles={styles.savedPaymentProfile}
                                            paymentMethodDto={accountBalancePayment.currentPaymentMethodDto}
                                            updateCardIsTokenized={accountBalancePayment.setIsCardNumberTokenized}
                                            ref={accountBalancePayment.savedPaymentProfile}
                                        />
                                    </GridItem>
                                </GridContainer>
                            </StyledWrapper>
                        )}
                        {accountBalancePayment.currentPaymentMethodDto &&
                            accountBalancePayment.currentPaymentMethodDto.isCreditCard && (
                                <StyledWrapper {...styles.subSection}>
                                    <CreditCardDetailsEntry
                                        optionalOnTokenize={(token, tokenData) =>
                                            accountBalancePayment.handleTokenizeRef.current(token, tokenData)
                                        }
                                        canSaveCard={accountBalancePayment.paymentOptions?.canStorePaymentProfile}
                                        iframe={"Spreedly"}
                                        paymetricFrameRef={accountBalancePayment.paymetricFrameRef}
                                        onSaveCardUpdate={accountBalancePayment.setSaveCard}
                                        onCardHolderNameUpdate={accountBalancePayment.setCardHolderName}
                                        onCardNumberUpdate={accountBalancePayment.setCardNumber}
                                        onCardTypeUpdate={accountBalancePayment.setCardType}
                                        onExpirationMonthUpdate={accountBalancePayment.setExpirationMonth}
                                        onExpirationYearUpdate={accountBalancePayment.setExpirationYear}
                                        onSecurityCodeUpdate={accountBalancePayment.setSecurityCode}
                                        availableCardTypes={accountBalancePayment.paymentOptions?.cardTypes ?? []}
                                        availableMonths={accountBalancePayment.paymentOptions?.expirationMonths ?? []}
                                        availableYears={accountBalancePayment.paymentOptions?.expirationYears ?? []}
                                        extendedStyles={styles.creditCardDetails}
                                        onResetPaymentMethodClick={() => {}}
                                        isIframeLoaded={accountBalancePayment.isIframeLoaded}
                                        error={
                                            accountBalancePayment.showFormErrors
                                                ? accountBalancePayment.paystandError
                                                : undefined
                                        }
                                        bankAccountLastFourDigits={accountBalancePayment.bankAccountLastFourDigits}
                                        creditCardLastFourDigits={accountBalancePayment.creditCardLastFourDigits}
                                        spreedlyConfig={accountBalancePayment.spreedlyConfig}
                                        spreedlyFrameStyleConfig={accountBalancePayment.spreedlyFrameStyleConfig}
                                        updateCardIsTokenized={accountBalancePayment.setIsCardNumberTokenized}
                                        updateMaskedCardNumber={accountBalancePayment.setMaskedCardNumber}
                                        ref={accountBalancePayment.creditCardDetails}
                                        onExpirationDateChange={() => {}}
                                        cardExpirationDateCondensed={false}
                                    />
                                </StyledWrapper>
                            )}

                        {accountBalancePayment.currentPaymentMethodDto?.isCreditCard && (
                            <StyledWrapper {...styles.subSection}>
                                <CreditCardBillingAddressEntry
                                    useBillTo={accountBalancePayment.useBillingAddress}
                                    onUseBillToChange={(event, value) =>
                                        accountBalancePayment.setUseBillingAddress(value)
                                    }
                                    billTo={accountBalancePayment.billToState}
                                    address1={accountBalancePayment.address1}
                                    address1Ref={accountBalancePayment.address1Ref}
                                    onAddress1Change={(event: React.ChangeEvent<HTMLInputElement>) =>
                                        accountBalancePayment.setAddress1(event.currentTarget.value)
                                    }
                                    address1Error={
                                        accountBalancePayment.showFormErrors
                                            ? accountBalancePayment.address1Error
                                            : undefined
                                    }
                                    country={accountBalancePayment.countryId}
                                    countryRef={accountBalancePayment.countryRef}
                                    onCountryChange={(_, value) => accountBalancePayment.setCountryId(value?.id ?? "")}
                                    countryError={
                                        accountBalancePayment.showFormErrors
                                            ? accountBalancePayment.countryError
                                            : undefined
                                    }
                                    state={accountBalancePayment.stateId}
                                    stateRef={accountBalancePayment.stateRef}
                                    onStateChange={(_, value) => accountBalancePayment.setStateId(value?.id ?? "")}
                                    stateError={
                                        accountBalancePayment.showFormErrors
                                            ? accountBalancePayment.stateError
                                            : undefined
                                    }
                                    city={accountBalancePayment.city}
                                    cityRef={accountBalancePayment.cityRef}
                                    onCityChange={event => accountBalancePayment.setCity(event.target.value)}
                                    cityError={
                                        accountBalancePayment.showFormErrors
                                            ? accountBalancePayment.cityError
                                            : undefined
                                    }
                                    postalCode={accountBalancePayment.postalCode}
                                    postalCodeRef={accountBalancePayment.postalCodeRef}
                                    onPostalCodeChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                        accountBalancePayment.setPostalCode(event.currentTarget.value)
                                    }
                                    postalCodeError={
                                        accountBalancePayment.showFormErrors
                                            ? accountBalancePayment.postalCodeError
                                            : undefined
                                    }
                                    availableCountries={accountBalancePayment.countries ?? []}
                                    availableStates={accountBalancePayment.selectedCountry?.states}
                                    extendedStyles={styles.creditCardAddress}
                                />
                            </StyledWrapper>
                        )}
                        {accountBalancePayment.currentPaymentMethodDto?.isPaymentProfile &&
                        !accountBalancePayment.currentPaymentMethodDto.isPaymentProfileExpired ? (
                            <StyledWrapper {...styles.subSection}>
                                <Typography {...styles.paymentProfileAddressHeading}>
                                    {translate("Credit Card Address")}
                                </Typography>
                                <PaymentProfileBillingAddress
                                    address={accountBalancePayment.currentPaymentMethodDto.billingAddress}
                                    extendedStyles={styles.paymentProfileBillingAddress}
                                />
                            </StyledWrapper>
                        ) : null}

                        <StyledWrapper {...styles.buttonWrapper}>
                            <Button {...styles.payButton}>{siteMessage("AccountBalance_ModalPay")}</Button>
                            {accountBalancePayment.errorMessage && (
                                <InfosBox type="error"> {accountBalancePayment.errorMessage}</InfosBox>
                            )}
                        </StyledWrapper>
                    </StyledFieldSet>
                </StyledForm>
            </LoadingOverlay>
        </Modal>
    );
};

const widgetModule: CustomWidgetModule = {
    component: AccountBalanceModel,
    definition: {
        group: "Payments",
        displayName: "Account Balance Modal",
    },
};

export default widgetModule;
